import React from 'react';
import { Button, Form, Input, message, Popover } from 'antd';
import { UserOutlined, LockOutlined, GlobalOutlined } from '@ant-design/icons';
import { connect } from 'dva';
import { injectIntl, WrappedComponentProps } from 'react-intl';

import CircularProgress from '@src/components/CircularProgress/index';
import CustomScrollbars from '@src/util/CustomScrollbars';
import languageData from '@src/containers/Topbar/languageData';
import { StoreState } from '@src/interfaces';
import logoBackground from '../assets/images/lpbank_thumb.png';

const FormItem = Form.Item;
const STORAGE_KEY = 'app_locale';

const getStoredLocale = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading locale from localStorage:', error);
  }
  return null;
};

class SignIn extends React.Component<
  {
    dispatch?: any;
    showMessage?: any;
    history?: any;
    authUser?: any;
    loader?: any;
    alertMessage?: any;
    locale?: any;
  } & WrappedComponentProps,
  {
    tokenCapcha?: string;
    captcha: string;
    accountKitToken?: string;
  }
> {
  constructor(props: any) {
    super(props);
    this.state = {
      captcha: '',
      accountKitToken: '',
    };
  }

  form = React.createRef<any>();

  languageMenu = () => (
    <CustomScrollbars className="gx-popover-lang-scroll">
      <ul className="gx-sub-popover">
        {languageData.map((language) => (
          <li
            className="gx-media gx-pointer"
            key={JSON.stringify(language)}
            onClick={() =>
              this.props.dispatch({
                type: 'settings/switchLanguage',
                payload: language,
              })
            }
          >
            <i className={`flag flag-24 gx-mr-2 flag-${language.icon}`} />
            <span className="gx-language-text">{language.name}</span>
          </li>
        ))}
      </ul>
    </CustomScrollbars>
  );

  handleSubmit = (values: Record<string, any>) => {
    const { dispatch } = this.props;
    dispatch({
      type: 'auth/showAuthLoader',
    });
    dispatch({
      type: 'auth/userSignIn',
      payload: {
        ...values,
        locale: getStoredLocale()?.locale || 'vi',
      },
    });
  };

  componentDidUpdate() {
    const { dispatch } = this.props;
    if (this.props.showMessage) {
      setTimeout(() => {
        dispatch({
          type: 'auth/hideMessage',
        });
      }, 100);
    }
    if (this.props.authUser !== null) {
      this.props.history.push('/');
    }
  }

  render() {
    const { showMessage, loader, alertMessage, locale, intl } = this.props;
    const t = (id: string) => intl.formatMessage({ id });

    return (
      <div className="signin-split">
        <aside className="signin-split__hero" aria-hidden={false}>
          <div className="signin-split__hero-glow signin-split__hero-glow--a" />
          <div className="signin-split__hero-glow signin-split__hero-glow--b" />
          <div className="signin-split__hero-pattern" />

          <div className="signin-split__hero-inner">
            <img
              className="signin-split__brand-mark"
              src={logoBackground}
              alt="LPBank"
            />
            <p className="signin-split__brand-name">LPBank</p>
            <h1 className="signin-split__headline">
              {t('app.userAuth.signIn')}
            </h1>
            <p className="signin-split__tagline">
              {t('app.userAuth.signInSubtitle')}
            </p>
          </div>
        </aside>

        <main className="signin-split__panel">
          {/* <div className="signin-split__lang">
            <Popover
              overlayClassName="gx-popover-horizantal"
              placement="bottomRight"
              content={this.languageMenu()}
              trigger="click"
            >
              <button type="button" className="signin-split__lang-btn">
                {locale?.icon ? (
                  <i className={`flag flag-24 flag-${locale.icon}`} />
                ) : (
                  <GlobalOutlined />
                )}
                <span>{locale?.name || 'Language'}</span>
              </button>
            </Popover>
          </div> */}

          <div className="signin-split__form-wrap">
            <p className="signin-split__eyebrow">
              {t('app.userAuth.welcomeBack')}
            </p>
            <h2 className="signin-split__form-title">
              {t('app.userAuth.login')}
            </h2>
            <p className="signin-split__form-desc">
              {t('app.userAuth.signInHint')}
            </p>

            <Form
              ref={this.form}
              onFinish={this.handleSubmit}
              className="signin-split__form"
              layout="vertical"
              requiredMark={false}
            >
              <FormItem
                name="username"
                initialValue=""
                label={t('app.userAuth.usernamePlaceholder')}
                rules={[
                  {
                    required: true,
                    message: t('app.userAuth.usernameRequired'),
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder={t('app.userAuth.usernamePlaceholder')}
                  prefix={<UserOutlined />}
                  autoComplete="username"
                />
              </FormItem>
              <FormItem
                name="password"
                initialValue=""
                label={t('app.userAuth.passwordPlaceholder')}
                rules={[
                  {
                    required: true,
                    message: t('app.userAuth.passwordRequired'),
                  },
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder={t('app.userAuth.passwordPlaceholder')}
                  prefix={<LockOutlined />}
                  autoComplete="current-password"
                />
              </FormItem>
              <FormItem className="signin-split__submit-item">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  className="signin-split__submit"
                  loading={!!loader}
                >
                  {t('app.userAuth.login')}
                </Button>
              </FormItem>
            </Form>

            <p className="signin-split__footer">{t('app.userAuth.getAccount')}</p>
          </div>

          {loader ? (
            <div className="signin-split__loader">
              <CircularProgress />
            </div>
          ) : null}
          {showMessage && alertMessage
            ? message.error(alertMessage.toString(), 10)
            : null}
        </main>
      </div>
    );
  }
}

const mapStateToProps = ({ auth, settings }: StoreState) => {
  const { loader, alertMessage, showMessage, authUser } = auth;
  const { locale } = settings;
  return { loader, alertMessage, showMessage, authUser, locale };
};

export default connect(mapStateToProps)(injectIntl(SignIn));
